import {
  doc,
  DocumentReference,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { Goal, NewGoalWithID } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { RoleOption } from 'shared/types/roles';
import { Timestamp } from 'shared/types/timestamp';

import { dateToFirestoreTimestamp } from './converters/date-to-firestore-timestamp';
import { newGoalConverter } from './converters/goal';
import {
  BatchDocuments,
  CollectionOptions,
  getAuthentication,
  getCollection,
  getDatabase,
} from './helpers';

export const createGoalsWithIds = async (
  goals: NewGoalWithID[],
): Promise<{ goals: Goal[]; oldIdToNewIdMap: Record<ID, ID> }> => {
  // Get the currently authenticated user
  const user = (await getAuthentication()).currentUser;

  if (!user) {
    throw Error('unauthenticated');
  }

  const goalCollection = getCollection(CollectionOptions.Goals);

  const { oldIdToNewIdMap, oldIdRefMap } = goals.reduce<{
    oldIdToNewIdMap: Record<ID, ID>;
    oldIdRefMap: Record<ID, DocumentReference>;
  }>(
    (acc, { id }) => {
      const ref = doc(goalCollection);
      acc.oldIdToNewIdMap[id] = ref.id;
      acc.oldIdRefMap[id] = ref;
      return acc;
    },
    { oldIdToNewIdMap: {}, oldIdRefMap: {} },
  );

  if (goals.length === 1) {
    // if only one goal is being created, we can use setDoc directly
    const goal = goals[0];
    const docRef = oldIdRefMap[goal.id];
    const data = {
      ...dateToFirestoreTimestamp(newGoalConverter(goal)),
      id: docRef.id,
      createdAt: dateToFirestoreTimestamp(new Date()),
      updatedAt: serverTimestamp() as unknown as Timestamp,
      completedAt: null,
      roles: { [RoleOption.Owner]: user.uid, [RoleOption.All]: [user.uid] },
    } as unknown as Goal;

    setDoc(docRef, data);

    return { goals: [data], oldIdToNewIdMap };
  }

  const batchDocuments = new BatchDocuments(getDatabase());

  const goalDocs = goals.map((goal) => {
    const docRef = oldIdRefMap[goal.id];

    return {
      ref: docRef,
      data: {
        ...dateToFirestoreTimestamp(newGoalConverter(goal)),
        id: docRef.id,
        createdAt: dateToFirestoreTimestamp(new Date()),
        updatedAt: serverTimestamp() as unknown as Timestamp,
        completedAt: null,
        roles: { [RoleOption.Owner]: user.uid, [RoleOption.All]: [user.uid] },
      } as unknown as Goal,
    };
  });

  goalDocs.forEach((docToAdd) => batchDocuments.addDocument(docToAdd));

  batchDocuments.commit();

  return {
    goals: goalDocs.map(({ data }) => data),
    oldIdToNewIdMap,
  };
};
